import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Capacitor } from "@capacitor/core";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { needsTermsReconsent } from "@researcher/shared";
import { ThemeProvider } from "./contexts/ThemeContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Eagerly loaded — needed for initial render
import Landing from "./pages/Landing";
import Chat from "./pages/Chat";
import WelcomePage from "./pages/WelcomePage";
import CookieConsentBanner from "./components/legal/CookieConsentBanner";
import TermsUpdateModal from "./components/legal/TermsUpdateModal";
import NotificationToast from "./components/NotificationToast";
import EmailVerificationBanner from "./components/EmailVerificationBanner";
import AdminRoute from "./components/admin/AdminRoute";
import { useReferralCapture } from "./hooks/useReferralCapture";
import { trackPageView } from "./utils/analytics";

// Lazy-loaded pages — split into separate chunks
const Advocacy = lazy(() => import("./pages/Advocacy"));
const Research = lazy(() => import("./pages/Library"));
const TopicDetail = lazy(() => import("./pages/TopicDetail"));
const LearningPathDetail = lazy(() => import("./pages/LearningPathDetail"));
const Quiz = lazy(() => import("./pages/Quiz"));
const CommunicationGuides = lazy(() => import("./pages/CommunicationGuides"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CommunityFeed = lazy(() => import("./pages/CommunityFeed"));
const CommunityNewPost = lazy(() => import("./pages/CommunityNewPost"));
const CommunityMyPosts = lazy(() => import("./pages/CommunityMyPosts"));
const CommunityPost = lazy(() => import("./pages/CommunityPost"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const PreferenceSettings = lazy(() => import("./pages/PreferenceSettings"));
const IntegrationSettings = lazy(() => import("./pages/IntegrationSettings"));
const Health = lazy(() => import("./pages/Health"));
const CycleTracker = lazy(() => import("./pages/CycleTracker"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminPosts = lazy(() => import("./pages/admin/AdminPosts"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminRomy = lazy(() => import("./pages/admin/AdminRomy"));
const AdminRomyAI = lazy(() => import("./pages/admin/AdminRomyAI"));
const AdminOperations = lazy(() => import("./pages/admin/AdminOperations"));
const AdminBrand = lazy(() => import("./pages/admin/AdminBrand"));
const AdminEmail = lazy(() => import("./pages/admin/AdminEmail"));
const AdminSystemHealth = lazy(() => import("./pages/admin/AdminSystemHealth"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminProductInsights = lazy(() => import("./pages/admin/AdminProductInsights"));
const AdminGrowth = lazy(() => import("./pages/admin/AdminGrowth"));
const AdminGrowthRadar = lazy(() => import("./pages/admin/AdminGrowthRadar"));
const AdminCommunityTopics = lazy(() => import("./pages/admin/AdminCommunityTopics"));
const AdminChangelog = lazy(() => import("./pages/admin/AdminChangelog"));
const AdminDemoAccounts = lazy(() => import("./pages/admin/AdminDemoAccounts"));
const YourData = lazy(() => import("./pages/YourData"));
const Trust = lazy(() => import("./pages/Trust"));
const AdvocacyLetterGenerator = lazy(() => import("./pages/AdvocacyLetterGenerator"));
const ForProviders = lazy(() => import("./pages/ForProviders"));
const SymptomPage = lazy(() => import("./pages/symptoms/SymptomPage"));
const ForConditionPage = lazy(() => import("./pages/forCondition/ForConditionPage"));
const ComparisonPage = lazy(() => import("./pages/comparison/ComparisonPage"));
const TrialMatcher = lazy(() => import("./pages/TrialMatcher"));
// Onboarding wizard replaced by conversational onboarding in Chat
const TreatmentJourney = lazy(() => import("./pages/TreatmentJourney"));
const AppointmentsPage = lazy(() => import("./pages/Appointments"));
const QuizFunnel = lazy(() => import("./pages/QuizFunnel"));
const Intake = lazy(() => import("./pages/Intake"));
const Feedback = lazy(() => import("./pages/Feedback"));
const ConditionPage = lazy(() => import("./pages/conditions/ConditionPage"));
const PeriodCalculator = lazy(() => import("./pages/tools/PeriodCalculator"));
const OvulationCalculator = lazy(() => import("./pages/tools/OvulationCalculator"));
const DueDateCalculator = lazy(() => import("./pages/tools/DueDateCalculator"));
const BMICalculator = lazy(() => import("./pages/tools/BMICalculator"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const InviteFriends = lazy(() => import("./pages/InviteFriends"));
const SleepTracker = lazy(() => import("./pages/health/SleepTracker"));
const NutritionTracker = lazy(() => import("./pages/health/NutritionTracker"));
const ActivityTracker = lazy(() => import("./pages/health/ActivityTracker"));
const IntimateHealth = lazy(() => import("./pages/health/IntimateHealth"));
const VitalsLabs = lazy(() => import("./pages/health/VitalsLabs"));
const MentalWellness = lazy(() => import("./pages/health/MentalWellness"));
const HealthProfile = lazy(() => import("./pages/health/HealthProfile"));
const GoalsPage = lazy(() => import("./pages/health/Goals"));
const GoalDetail = lazy(() => import("./pages/health/GoalDetail"));
const CheckIns = lazy(() => import("./pages/health/CheckIns"));
const MealPlanDetail = lazy(() => import("./pages/health/MealPlanDetail"));
const PublicMealPlan = lazy(() => import("./pages/PublicMealPlan"));
const GetStartedMealPlan = lazy(() => import("./pages/GetStartedMealPlan"));
const Documents = lazy(() => import("./pages/health/Documents"));

const isNative = Capacitor.isNativePlatform();

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppRoutes() {
  useReferralCapture();
  const location = useLocation();
  const { currentUser, loading, onboardingCompleted, acceptedTermsVersion, emailVerified } = useAuth();

  // Product analytics: record which feature/tool the user is viewing.
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Signed-in users → Chat as home; everyone else → Landing/about page
  const Home = currentUser ? Chat : Landing;

  // Native app: show WelcomePage until signed in
  if (isNative && !currentUser && !loading) {
    return <WelcomePage />;
  }

  // Native app: show nothing while auth is loading
  if (isNative && loading) {
    return null;
  }

  // Unverified email/password users see a non-blocking banner instead of a
  // hard gate. Specific actions (community posting, data export) check
  // emailVerified themselves.
  const showVerificationBanner = currentUser && !loading && !emailVerified;

  // Block users who haven't accepted the current terms (covers new OAuth
  // users who bypassed the checkbox AND existing users after a version bump)
  if (currentUser && !loading &&
    needsTermsReconsent(acceptedTermsVersion)) {
    const onLegalPage = ['/terms', '/privacy'].includes(location.pathname);
    return (
      <Suspense fallback={<PageLoader />}>
        {!onLegalPage && <TermsUpdateModal />}
        <Routes>
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="*" element={null} />
        </Routes>
      </Suspense>
    );
  }

  // New users: redirect initial landing to chat, but don't lock them in
  if (currentUser && !loading && !onboardingCompleted && location.pathname === '/') {
    return <Navigate to="/chat" replace />;
  }

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        {/* key forces a full remount of all routes when the user changes.
            Prevents cross-account state leaks (e.g. old user's chat opening
            message showing for a newly signed-in user). */}
        <Routes key={currentUser?.uid || 'anon'}>
          {/* Public routes */}
          <Route path="/about" element={<Landing />} />
          <Route path="/advocacy" element={<Advocacy />} />
          <Route path="/advocacy/letter-generator" element={<AdvocacyLetterGenerator />} />
          <Route path="/communication-guides" element={<CommunicationGuides />} />
          <Route path="/research" element={<Research />} />
          <Route path="/research/topics/:slug" element={<TopicDetail />} />
          <Route path="/research/learning/:pathId" element={<LearningPathDetail />} />
          <Route path="/trials" element={<TrialMatcher />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/quiz" element={<Quiz />} />
          <Route path="/get-started" element={<QuizFunnel />} />
          <Route path="/intake" element={<Intake />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* Public share pages (no auth, no app nav) */}
          <Route path="/p/meal-plan/:token" element={<PublicMealPlan />} />
          <Route path="/get-started/meal-plan" element={<GetStartedMealPlan />} />

          {/* Community routes */}
          <Route path="/community" element={<CommunityFeed />} />
          <Route path="/community/new" element={<CommunityNewPost />} />
          <Route path="/community/my-posts" element={<CommunityMyPosts />} />
          <Route path="/community/post/:postId" element={<CommunityPost />} />
          <Route path="/community/:topicSlug" element={<CommunityFeed />} />

          {/* Health tracker */}
          <Route path="/health" element={<Health />} />
          <Route path="/health/cycle" element={<CycleTracker />} />
          <Route path="/health/treatments" element={<TreatmentJourney />} />
          <Route path="/health/sleep" element={<SleepTracker />} />
          <Route path="/health/nutrition" element={<NutritionTracker />} />
          <Route path="/health/activity" element={<ActivityTracker />} />
          <Route path="/health/intimate" element={<IntimateHealth />} />
          <Route path="/health/vitals" element={<VitalsLabs />} />
          <Route path="/health/mental" element={<MentalWellness />} />
          <Route path="/health/goals" element={<GoalsPage />} />
          <Route path="/health/goals/:goalId" element={<GoalDetail />} />
          <Route path="/health/profile" element={<HealthProfile />} />
          <Route path="/health/appointments" element={<AppointmentsPage />} />
          <Route path="/health/check-ins" element={<CheckIns />} />
          <Route path="/health/meal-plans/:planId" element={<MealPlanDetail />} />
          <Route path="/health/documents" element={<Documents />} />

          {/* Admin routes */}
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
          <Route path="/admin/posts" element={<AdminRoute><AdminPosts /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute adminOnly><AdminUsers /></AdminRoute>} />
          <Route path="/admin/romy" element={<AdminRoute adminOnly><AdminRomy /></AdminRoute>} />
          <Route path="/admin/romy-ai" element={<AdminRoute adminOnly><AdminRomyAI /></AdminRoute>} />
          <Route path="/admin/operations" element={<AdminRoute adminOnly><AdminOperations /></AdminRoute>} />
          <Route path="/admin/brand" element={<AdminRoute adminOnly><AdminBrand /></AdminRoute>} />
          <Route path="/admin/email" element={<AdminRoute adminOnly><AdminEmail /></AdminRoute>} />
          <Route path="/admin/system-health" element={<AdminRoute adminOnly><AdminSystemHealth /></AdminRoute>} />
          <Route path="/admin/referrals" element={<AdminRoute adminOnly><AdminReferrals /></AdminRoute>} />
          <Route path="/admin/product-insights" element={<AdminRoute adminOnly><AdminProductInsights /></AdminRoute>} />
          <Route path="/admin/growth" element={<AdminRoute adminOnly><AdminGrowth /></AdminRoute>} />
          <Route path="/admin/growth-radar" element={<AdminRoute adminOnly><AdminGrowthRadar /></AdminRoute>} />
          <Route path="/admin/community-topics" element={<AdminRoute adminOnly><AdminCommunityTopics /></AdminRoute>} />
          <Route path="/admin/changelog" element={<AdminRoute adminOnly><AdminChangelog /></AdminRoute>} />
          <Route path="/admin/demo-accounts" element={<AdminRoute adminOnly><AdminDemoAccounts /></AdminRoute>} />

          {/* Settings routes */}
          <Route path="/settings/account" element={<AccountSettings />} />
          <Route path="/settings/notifications" element={<NotificationSettings />} />
          <Route path="/settings/preferences" element={<PreferenceSettings />} />
          <Route path="/settings/integrations" element={<IntegrationSettings />} />
          <Route path="/settings/your-data" element={<YourData />} />

          {/* Condition pages */}
          <Route path="/conditions/:slug" element={<ConditionPage />} />

          {/* Provider one-pager */}
          <Route path="/for-providers" element={<ForProviders />} />
          <Route path="/providers" element={<ForProviders />} />

          {/* Symptom-led landing pages */}
          <Route path="/symptoms/:slug" element={<SymptomPage />} />

          {/* For-condition conversion pages */}
          <Route path="/for-endo" element={<ForConditionPage />} />
          <Route path="/for-pcos" element={<ForConditionPage />} />
          <Route path="/for-pmdd" element={<ForConditionPage />} />
          <Route path="/for-perimenopause" element={<ForConditionPage />} />
          <Route path="/for-fertility" element={<ForConditionPage />} />

          {/* Comparison pages */}
          <Route path="/vs-flo" element={<ComparisonPage />} />
          <Route path="/vs-clue" element={<ComparisonPage />} />
          <Route path="/vs-stardust" element={<ComparisonPage />} />
          <Route path="/flo-alternative" element={<ComparisonPage />} />

          {/* Health calculators */}
          <Route path="/tools/period-calculator" element={<PeriodCalculator />} />
          <Route path="/tools/ovulation-calculator" element={<OvulationCalculator />} />
          <Route path="/tools/due-date-calculator" element={<DueDateCalculator />} />
          <Route path="/tools/bmi-calculator" element={<BMICalculator />} />

          {/* Invite & trust pages */}
          <Route path="/invite" element={<InviteFriends />} />
          <Route path="/trust" element={<Trust />} />
          <Route path="/account/delete" element={<DeleteAccount />} />
          {/* /data/delete removed — data deletion only via account deletion */}

          <Route path="/" element={<Home />} />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <CookieConsentBanner />
      <NotificationToast />
      {showVerificationBanner && <EmailVerificationBanner />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
